import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { widgetType } from '../../../constants/widget-type.constants';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { CookieAppService } from '@shared/common/services/cookie-app/cookie-app.service';
import { DateService } from '../../../core/services/date-service/date.service';
import differenceInDays from 'date-fns/differenceInDays';
import setHours from 'date-fns/setHours';
import { BookingParamsService } from '../../../core/services/booking-params/booking-params.service';
import { IBookingParams } from '../../../core/interfaces/booking-params';
import { LocationService } from '../../../core/services/location/location.service';
import { ITaxation } from '../../../core/interfaces/taxation';
import { languages } from '../../../constants/language.constants';
import { IOffer } from '../../../core/interfaces/offer';
import { takeUntil } from 'rxjs/operators';
import { IPropertyImage } from '../../../core/interfaces/property';
import { placeholderTypes } from '../../../constants/placeholder-types.constants';
import { IGuestsData } from '../../../core/interfaces/guests-data';

@Component({
  selector: 'app-booking-widget',
  templateUrl: './booking-widget.component.html',
  styleUrls: ['./booking-widget.component.scss'],
  standalone: false
})
export class BookingWidgetComponent implements OnDestroy {
  selectedDatesLabel = '';
  selectedDates: Date[] = [];
  daysCount = 0;
  arrivalDate: Date;
  departureDate: Date;
  voucher: string;
  bookingProperty = false;
  isAppliedVoucher = false;
  widgetType = widgetType;
  confirmationDataLoaded = false;
  confirmationTotal: number;
  propertyName: string;
  allOffers: IOffer[];
  isFetching = true;
  fetchableParams: any = {};
  hiddenContent = true;
  checkin: string;
  checkout: string;
  city: number;
  childrenAgesArr = [];
  bookingParams: IBookingParams;
  isOpened = false;
  placeholderTypes = placeholderTypes;
  private readonly _destruction$: Subject<void> = new Subject<void>();

  @Input() rooms = 1;
  @Input() adults = 1;
  @Input() childrenAges: any = '';
  @Input() applicableCosts: ITaxation;
  @Input() ratePlan: IOffer;
  @Input() currency: string;
  @Input() name: string;
  @Input() openGuestPickerModalOnMobile = false;
  @Input() isMobile: boolean;
  @Input() propertyId: number;
  @Input() unitGroupId: number;
  @Input() classList: string;
  @Input() isDisabled: boolean;
  @Input() widgetSpecifiedType: widgetType;
  @Input() bwImages: IPropertyImage[];
  @Input() dataFetched: boolean;
  @Input() streetAddress: string;
  @Input() isEmptyDates: boolean;
  @Input() updateGuestWithoutRequest: boolean;
  @Input() currentLang: languages;
  @Input() isExtension: boolean;
  @Input() readOnly: boolean;
  @Input() btnText = 'booking-widget-book';
  @Input() guestCtaText: string;
  @Input() calendarType: 'pdp' | 'listing' | 'other';
  @Input() set isLoading(loading) {
    this.isFetching = loading;
  }
  @Input() set voucherCode(voucherCode: string) {
    if (!!voucherCode && voucherCode.length) {
      this.isAppliedVoucher = true;
      this.voucher = voucherCode;
    }
  }
  @Input() set offers(offers: IOffer[]) {
    this.allOffers = offers;
  }

  @Output() bookingSubmitted: EventEmitter<any> = new EventEmitter();
  @Output() updateBookingEmitter: EventEmitter<any>;
  @Output() voucherCodeApplied = new EventEmitter<string>();
  @Output() widgetPickersOpeningStatus: EventEmitter<boolean> = new EventEmitter();
  @Output() datePickersOpeningStatus: EventEmitter<boolean> = new EventEmitter();

  constructor(
    public router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly bookingParamsService: BookingParamsService,
    private readonly locationService: LocationService,
    public cookieAppService: CookieAppService,
    public dateService?: DateService
  ) {
    this.updateBookingEmitter = new EventEmitter<any>();

    this.bookingParamsService
      .selectBookingParams()
      .pipe(takeUntil(this._destruction$))
      .subscribe((params) => {
        if (params) {
          params = { ...params };

          this.bookingParams = params;
          this.adults = +params.guests || 1;
          this.rooms = +params.rooms || 1;
          this.childrenAges = params.childrenAges != null ? params.childrenAges.toString() : '';
          this.city = params.city;
          this.checkin = params.checkin;
          this.checkout = params.checkout;
          this.arrivalDate = new Date(params.checkin + 'T00:00');
          this.departureDate = new Date(this.checkout + 'T00:00');

          if (params.property) {
            this.propertyId = params.property;
          }
          if (params.unitgroup) {
            this.unitGroupId = params.unitgroup;
          }

          this.getDatesLabels(this.checkin, this.checkout);

          const { city, property, unitgroup, checkin, checkout, rooms, guests, childrenAges, rateplan } = params;
          this.fetchableParams = {
            city,
            property,
            unitgroup,
            checkin,
            checkout,
            rooms,
            guests,
            childrenAges,
            rateplan
          };

          this.ensurePropertyAndUnitGroupPreserved();

          this.updateBookingEmitter.emit(this.fetchableParams);

          this.childrenAgesArr =
            ![undefined, null].includes(this.childrenAges) && this.childrenAges.toString() != ''
              ? this.childrenAges
                  .toString()
                  .split(',')
                  .map((x) => +x)
              : [];
        } else {
          this.bookingParams = this.locationService.urlParamsToObj(this.locationService.getPathParams());
          this.bookingParamsService.dispatchAddBookingParams(this.bookingParams);
        }
      });
  }

  ngOnDestroy() {
    this._destruction$.next();
    this._destruction$.complete();
  }

  bookProperty() {
    this.bookingSubmitted.emit();
  }

  getDatesLabels(checkin: string, checkout: string) {
    if (checkin) {
      this.selectedDates = [setHours(new Date(checkin + 'T00:00'), 0), setHours(new Date(checkout + 'T00:00'), 0)];
      this.selectedDatesLabel = `${this.dateService.formatDateForHeroPlaceholder(
        checkin
      )} - ${this.dateService.formatDateForHeroPlaceholder(checkout)}`;
      this.daysCount = differenceInDays(
        setHours(new Date(checkout + 'T00:00'), 0),
        setHours(new Date(checkin + 'T00:00'), 0)
      );
    } else {
      this.selectedDatesLabel = '';
      this.selectedDates = [];
      this.daysCount = 0;
    }
  }

  openDetails(isHidden: boolean) {
    this.hiddenContent = isHidden;
  }

  private ensurePropertyAndUnitGroupPreserved(): void {
    if (!this.fetchableParams.property) {
      if (this.propertyId) {
        this.fetchableParams.property = this.propertyId;
      } else if (this.bookingParams?.property) {
        this.fetchableParams.property = this.bookingParams.property;
      }
    }

    if (!this.fetchableParams.unitgroup) {
      if (this.unitGroupId) {
        this.fetchableParams.unitgroup = this.unitGroupId;
      } else if (this.bookingParams?.unitgroup) {
        this.fetchableParams.unitgroup = this.bookingParams.unitgroup;
      }
    }
  }

  navigateToCheckout() {
    this.router.navigate([`/listings/checkout`], {
      queryParams: { ...this.fetchableParams },
      queryParamsHandling: 'merge'
    });
  }

  processBookedDates(dates: Date[]): void {
    const arrival = this.dateService.formatDateForQuery(dates[0]);
    const departure = this.dateService.formatDateForQuery(dates[dates.length - 1]);
    this.arrivalDate = dates[0];
    this.departureDate = dates[dates.length - 1];
    this.selectedDatesLabel = `${this.dateService.formatDateForHeroPlaceholder(
      arrival
    )} - ${this.dateService.formatDateForHeroPlaceholder(departure)}`;
    this.selectedDates = [setHours(dates[0], 0), setHours(dates[dates.length - 1], 0)];
    this.daysCount = dates.length - 1;
    this.fetchableParams.checkin = arrival;
    this.fetchableParams.checkout = departure;
    this.fetchableParams.isFetching = this.isFetching;
    this.ensurePropertyAndUnitGroupPreserved();

    this.updateBookingParams();
    this.updateBookingEmitter.emit(this.fetchableParams);
  }

  updateGuestsData(data: IGuestsData): void {
    this.fetchableParams.rooms = data.rooms;
    this.fetchableParams.guests = data.adults;
    this.fetchableParams.childrenAges = data.childrenAges;
    this.fetchableParams.isFetching = this.isFetching;
    this.childrenAges = data.childrenAges;
    this.adults = data.adults;
    this.rooms = data.rooms;
    this.ensurePropertyAndUnitGroupPreserved();

    this.updateBookingEmitter.emit(this.fetchableParams);
    this.updateBookingParams();
  }

  updateBookingParams() {
    const { isFetching, ...others } = this.fetchableParams;
    const currentRoute = this.locationService.urlParamsToObj(this.locationService.getPathParams());

    this.bookingParams = { ...this.bookingParams, ...this.fetchableParams };

    if (!this.bookingParams.childrenAges) {
      this.bookingParams.childrenAges = null;
    }
    if (!others.childrenAges) {
      others.childrenAges = null;
    }
    this.bookingParamsService.dispatchAddBookingParams(
      this.bookingParams,
      others,
      this.activatedRoute,
      currentRoute,
      true
    );
  }
  toggleBookingWidget() {
    this.isOpened = !this.isOpened;
  }

  onDatePickerStatus(status: boolean) {
    this.datePickersOpeningStatus.emit(status);
  }

  onGuestDropdownStatus(status: boolean) {
    this.widgetPickersOpeningStatus.emit(status);
  }
}
